import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Search,
  ArrowLeft,
  Edit2,
  Trash2,
  Plus,
  Users,
} from "lucide-react";
import Header from "../../components/Header";
import LoadingLogo from "../../components/LoadingLogo";
import {
  getAuthenticatedAxios,
  buildApiPath,
  API_ENDPOINTS,
} from "../../utils/api";

function CompanyManagement() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  // Load companies
  const loadCompanies = async () => {
    try {
      setError(null);
      const axios = getAuthenticatedAxios();
      const response = await axios.get(
        buildApiPath(API_ENDPOINTS.ADMIN.COMPANIES),
      );

      // Handle paginated response from backend
      // Backend returns { items: [], meta: {} } structure
      const companiesData = response.data.items || [];
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
    } catch (err) {
      console.error("Failed to load companies:", err);
      setError("Failed to load companies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  // Filter companies based on search
  const filteredCompanies = companies.filter((company) => {
    const name = company.name || company.Name || "";
    const description = company.description || company.Description || "";

    return (
      searchTerm === "" ||
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Handle add/edit company
  const handleSaveCompany = async () => {
    try {
      const axios = getAuthenticatedAxios();

      if (editingCompany) {
        // Update existing company
        const companyId = editingCompany.id || editingCompany.CompanyId;
        await axios.put(
          `${buildApiPath(API_ENDPOINTS.ADMIN.COMPANIES)}/${companyId}`,
          formData,
        );
        setSuccessMessage("Company updated successfully");
      } else {
        // Create new company
        await axios.post(buildApiPath(API_ENDPOINTS.ADMIN.COMPANIES), formData);
        setSuccessMessage("Company created successfully");
      }

      setShowAddModal(false);
      setEditingCompany(null);
      setFormData({ name: "", description: "" });
      await loadCompanies();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(
        `Failed to save company: ${err.response?.data?.detail || err.message}`,
      );
    }
  };

  // Handle delete company
  const handleDeleteCompany = async (companyId, companyName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${companyName}? This will also delete all associated users.`,
      )
    ) {
      return;
    }

    try {
      const axios = getAuthenticatedAxios();
      await axios.delete(
        `${buildApiPath(API_ENDPOINTS.ADMIN.COMPANIES)}/${companyId}`,
      );

      setSuccessMessage("Company deleted successfully");
      await loadCompanies();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setError(
        `Failed to delete company: ${err.response?.data?.detail || err.message}`,
      );
    }
  };

  // Handle edit click
  const handleEditClick = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name || company.Name || "",
      description: company.description || company.Description || "",
    });
    setShowAddModal(true);
  };

  // View company users
  const handleViewUsers = (companyId) => {
    navigate(`/admin/users?company=${companyId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate("/admin")}
                className="mr-4 p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  <Building2 className="h-8 w-8 mr-3 text-blue-600" />
                  Company Management
                </h1>
                <p className="mt-1 text-gray-600">
                  Manage companies and their settings
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setEditingCompany(null);
                setFormData({ name: "", description: "" });
                setShowAddModal(true);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Company
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Companies Table */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingLogo size="md" message="Loading companies..." />
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No companies found</p>
          </div>
        ) : (
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Users
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCompanies.map((company, index) => {
                  // Handle both snake_case and PascalCase field names
                  const companyId = company.id || company.CompanyId;
                  const companyName = company.name || company.Name;
                  const description =
                    company.description ||
                    company.Description ||
                    "No description";
                  const userCount =
                    company.user_count || company.UserCount || 0;
                  const createdAt = company.created_at || company.CreatedAt;
                  const uniqueKey = companyId
                    ? `${companyId}-${index}`
                    : `company-${index}`;

                  return (
                    <tr key={uniqueKey} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building2 className="h-5 w-5 mr-3 text-gray-400" />
                          <div className="text-sm font-medium text-gray-900">
                            {companyName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 max-w-xs truncate">
                          {description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewUsers(companyId)}
                          className="flex items-center text-blue-600 hover:text-blue-800"
                        >
                          <Users className="h-4 w-4 mr-1" />
                          {userCount} users
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {createdAt
                          ? new Date(createdAt).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEditClick(company)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Edit Company"
                          >
                            <Edit2 className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteCompany(companyId, companyName)
                            }
                            className="text-red-600 hover:text-red-900"
                            title="Delete Company"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">
              {editingCompany ? "Edit Company" : "Add New Company"}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter company name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Enter company description"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingCompany(null);
                  setFormData({ name: "", description: "" });
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCompany}
                disabled={!formData.name}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingCompany ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyManagement;
